import React, { useState } from 'react';
import { Mail, Phone, MapPin, Github, Linkedin, ExternalLink, Briefcase, GraduationCap, Code, Star, ArrowUpRight, Menu, X } from 'lucide-react';
import emailjs from 'emailjs-com';
import HeroSection from './components/portfolio/HeroSection';

const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const navItems = [
        { label: "About", href: "#about" },
        { label: "Skills", href: "#skills" },
        { label: "Experience", href: "#experience" },
        { label: "Projects", href: "#projects" },
        { label: "Education", href: "#education" },
        { label: "Contact", href: "#contact" }
    ];

    return (
        <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-slate-950/70 backdrop-blur">
            <div className="mx-auto flex max-w-screen-xl items-center justify-between px-4 py-3 sm:px-6">
                <a href="#top" className="flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 text-sm font-bold text-white shadow-sm">
                        KS
                    </div>
                    <div className="hidden sm:block">
                        <div className="text-sm font-semibold text-white leading-tight">Kehinde Salaudeen</div>
                        <div className="text-xs text-slate-300 leading-tight">Senior Frontend Software Developer</div>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-6">
                    {navItems.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            className="text-sm text-slate-200/90 hover:text-white transition-colors"
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setIsOpen((v) => !v)}
                        aria-label={isOpen ? "Close menu" : "Open menu"}
                        className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-white hover:bg-white/15 transition-colors"
                    >
                        {isOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                    <a
                        href="/CVs/kehinde-cv.pdf"
                        download
                        className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 transition-colors"
                    >
                        Download CV
                        <ArrowUpRight className="h-4 w-4" />
                    </a>
                </div>
            </div>

            {isOpen && (
                <div className="md:hidden border-t border-white/10 bg-slate-950/90 backdrop-blur">
                    <div className="mx-auto max-w-screen-xl px-4 py-3 sm:px-6">
                        <div className="grid grid-cols-2 gap-2">
                            {navItems.map((item) => (
                                <a
                                    key={item.href}
                                    href={item.href}
                                    onClick={() => setIsOpen(false)}
                                    className="rounded-xl bg-white/5 px-3 py-2 text-sm font-medium text-slate-200 hover:bg-white/10 hover:text-white transition-colors"
                                >
                                    {item.label}
                                </a>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
};

const AboutSection = () => {
    const highlights = [
        "Shipped cross-platform products serving 25,000+ users",
        "Reduced infrastructure costs by 90% through query optimization and caching",
        "Improved app-store ratings from 3.8★ → 4.5★ via rapid OTA releases",
        "Accelerate delivery with AI-assisted workflows (Cursor, Claude Code)"
    ];

    return (
        <section id="about" className="scroll-mt-24 bg-white py-24">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">About</h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-600">
                        Building scalable products with a focus on performance, reliability, and user experience.
                    </p>
                </div>

                <div className="mt-14 grid gap-10 lg:grid-cols-2 lg:items-start">
                    <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
                        <p className="text-base sm:text-lg text-slate-700 leading-relaxed">
                            Senior Frontend Software Developer with 6 years of experience building high-performance, scalable web and mobile applications across food-tech, fintech, oil & gas, edtech, and tourism.
                        </p>

                        <p className="mt-5 text-base sm:text-lg text-slate-700 leading-relaxed">
                            Expert in React.js, React Native, Vue.js, and TypeScript, complemented by backend proficiency in Node.js, Firebase (Firestore, Realtime Database, Cloud Functions), and AWS. I specialize in reusable component systems, optimized rendering pipelines, and efficient data-fetching with GraphQL, React Query, and React Hook Form.
                        </p>

                        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {highlights.map((highlight, index) => (
                                <div key={index} className="flex items-start gap-3 rounded-2xl bg-slate-50 p-4">
                                    <Star className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm sm:text-base text-slate-700 leading-snug">{highlight}</span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="rounded-3xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-7 shadow-sm">
                            <div className="text-3xl sm:text-4xl font-bold text-slate-900">6+</div>
                            <div className="mt-1 text-sm text-slate-600">Years Experience</div>
                        </div>
                        <div className="rounded-3xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-7 shadow-sm">
                            <div className="text-3xl sm:text-4xl font-bold text-slate-900">25K+</div>
                            <div className="mt-1 text-sm text-slate-600">Users Served</div>
                        </div>
                        <div className="rounded-3xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-7 shadow-sm">
                            <div className="text-3xl sm:text-4xl font-bold text-slate-900">90%</div>
                            <div className="mt-1 text-sm text-slate-600">Infra Cost Reduction</div>
                        </div>
                        <div className="rounded-3xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 p-7 shadow-sm">
                            <div className="text-3xl sm:text-4xl font-bold text-slate-900">4.5★</div>
                            <div className="mt-1 text-sm text-slate-600">App Rating Lift</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const SkillsSection = () => {
    const skillCategories = [
        {
            title: "Languages",
            skills: ["TypeScript", "JavaScript (ES6+)", "Rust", "Python"],
            color: "blue"
        },
        {
            title: "Frontend & Mobile",
            skills: ["React.js", "Next.js", "React Native", "Vue.js", "Nuxt.js", "Flutter"],
            color: "purple"
        },
        {
            title: "Backend & Cloud",
            skills: ["Node.js", "Express.js", "Firebase (Auth/CF)", "AWS"],
            color: "green"
        },
        {
            title: "Data & APIs",
            skills: ["GraphQL", "Apollo", "REST APIs", "Firestore", "Realtime Database"],
            color: "orange"
        },
        {
            title: "State & Forms",
            skills: ["Redux Toolkit", "TanStack Query", "React Hook Form", "Context API", "Pinia/Vuex"],
            color: "pink"
        },
        {
            title: "Tooling & DevOps",
            skills: ["Vercel", "Netlify", "Git", "Docker", "CI/CD", "Figma"],
            color: "indigo"
        },
        {
            title: "AI-Assisted Dev",
            skills: ["Cursor", "Claude Code", "Refactoring", "Scaffolding", "Debugging"],
            color: "teal"
        }
    ];

    const getColorClasses = (color) => {
        const colors = {
            blue: "bg-blue-50 border-blue-200 text-blue-800",
            purple: "bg-purple-50 border-purple-200 text-purple-800",
            green: "bg-green-50 border-green-200 text-green-800",
            orange: "bg-orange-50 border-orange-200 text-orange-800",
            pink: "bg-pink-50 border-pink-200 text-pink-800",
            indigo: "bg-indigo-50 border-indigo-200 text-indigo-800",
            teal: "bg-teal-50 border-teal-200 text-teal-800"
        };
        return colors[color];
    };

    const getIconColorClass = (color) => {
        const colors = {
            blue: "text-blue-500",
            purple: "text-purple-500",
            green: "text-green-500",
            orange: "text-orange-500",
            pink: "text-pink-500",
            indigo: "text-indigo-500",
            teal: "text-teal-500"
        };
        return colors[color];
    };

    return (
        <section id="skills" className="scroll-mt-24 bg-slate-50 py-24">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Skills</h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-600">
                        A modern stack for building fast, reliable web and mobile products.
                    </p>
                </div>

                <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {skillCategories.map((category, index) => (
                        <div key={index} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-slate-50">
                                    <Code className={`h-5 w-5 ${getIconColorClass(category.color)}`} />
                                </div>
                                <h3 className="text-lg font-semibold text-slate-900">{category.title}</h3>
                            </div>
                            <div className="mt-5 flex flex-wrap gap-2">
                                {category.skills.map((skill, skillIndex) => (
                                    <span key={skillIndex} className={`px-3 py-1 rounded-full text-sm border ${getColorClasses(category.color)}`}>
                                        {skill}
                                    </span>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const ExperienceSection = () => {
    const experiences = [
        {
            company: "Independent Purchasing Company (IPC)",
            role: "Senior Frontend Developer",
            period: "Feb 2025 – Present",
            achievements: [
                "Architected and shipped a cross-border React Native ordering app for 10,000+ active users (Nigeria & Kenya)",
                "Optimized data-fetching with React Query, reducing response times by 40%",
                "Built multi-currency ordering flows and localized UX for NGN and KES",
                "Integrated GraphQL APIs to reduce over-fetching by ~45% and accelerate feature delivery"
            ],
            color: "blue"
        },
        {
            company: "DSTRCT (Europe)",
            role: "Software Developer (Contract)",
            period: "Feb 2026 – Apr 2026",
            achievements: [
                "Led frontend engineering for an AI co-pilot creator-growth platform using React.js and TypeScript",
                "Built conversational AI interfaces for retention breakdowns, benchmarks, and growth-score explanations",
                "Implemented async recomputation flows and robust loading-state orchestration across AI surfaces",
                "Shipped reusable component libraries to speed up feature delivery across teams"
            ],
            color: "purple"
        },
        {
            company: "Urban Eats",
            role: "Lead Software Developer",
            period: "Jun 2023 – Aug 2025",
            achievements: [
                "Led and scaled a multi-platform food-delivery product serving 15,000+ active users",
                "Built reusable Vue.js web and React Native mobile systems for feature parity",
                "Integrated Firebase Realtime Database for live driver/order tracking with sub-second latency",
                "Reduced infrastructure costs by 90% through query optimization and client-side caching"
            ],
            color: "green"
        },
        {
            company: "New Cross Pan Ocean",
            role: "Software Developer (Contract)",
            period: "Jun 2024 – Dec 2024",
            achievements: [
                "Built an operational data-capture system reducing manual data entry by 90%",
                "Integrated Firestore for real-time sync between field engineers and management dashboards",
                "Automated validation/reporting workflows with Cloud Functions for error-free submissions",
                "Optimized large time-series dashboards, improving chart performance by ~45%"
            ],
            color: "orange"
        },
        {
            company: "ReachAfrika",
            role: "Senior Frontend Developer",
            period: "Oct 2022 – Nov 2024",
            achievements: [
                "Built secure fintech dashboards and high-volume payment workflows",
                "Created reusable component libraries and scalable design systems for UI consistency",
                "Implemented secure JWT session management with automated token refresh",
                "Reduced dashboard load times by 35% via bundle and rendering optimizations"
            ],
            color: "pink"
        },
        {
            company: "Quantum Analytics",
            role: "Frontend Developer",
            period: "Nov 2022 – Aug 2023",
            achievements: [
                "Implemented payment gateway systems contributing to a 75% increase in processed revenue",
                "Built learner dashboards for course progress tracking and engagement analytics",
                "Delivered region-aware content experiences to improve localization",
                "Improved form performance and correctness using React Hook Form"
            ],
            color: "indigo"
        },
        {
            company: "Tourbounty",
            role: "Frontend Developer",
            period: "Jun 2020 – Jul 2022",
            achievements: [
                "Built a responsive tourism web platform in React.js with performance-focused architecture",
                "Shipped a TikTok-style snap-scroll browsing experience increasing session duration by 25%",
                "Delivered pixel-perfect interfaces from Figma designs with high fidelity",
                "Improved Core Web Vitals via code-splitting and lazy loading"
            ],
            color: "teal"
        }
    ];

    const getPillClasses = (color) => {
        const colors = {
            blue: "bg-blue-100 text-blue-800",
            purple: "bg-purple-100 text-purple-800",
            green: "bg-green-100 text-green-800",
            orange: "bg-orange-100 text-orange-800",
            pink: "bg-pink-100 text-pink-800",
            indigo: "bg-indigo-100 text-indigo-800",
            teal: "bg-teal-100 text-teal-800"
        };
        return colors[color];
    };

    const getDotClass = (color) => {
        const colors = {
            blue: "bg-blue-500",
            purple: "bg-purple-500",
            green: "bg-green-500",
            orange: "bg-orange-500",
            pink: "bg-pink-500",
            indigo: "bg-indigo-500",
            teal: "bg-teal-500"
        };
        return colors[color];
    };

    return (
        <section id="experience" className="scroll-mt-24 bg-white py-24">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Experience</h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-600">
                        A track record of shipping products across food-tech, fintech, oil & gas, edtech, and creator-tech.
                    </p>
                </div>

                <div className="mt-14 relative">
                    <div className="absolute left-4 top-0 bottom-0 w-px bg-slate-200" />
                    <div className="space-y-8">
                        {experiences.map((exp, index) => (
                            <div key={index} className="relative pl-12">
                                <div className={`absolute left-[10px] top-7 h-3 w-3 rounded-full ${getDotClass(exp.color)}`} />
                                <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm hover:shadow-md transition-shadow">
                                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                                        <div>
                                            <h3 className="text-xl sm:text-2xl font-bold text-slate-900">{exp.company}</h3>
                                            <div className="mt-2 flex items-center gap-2 text-slate-600">
                                                <Briefcase className="h-4 w-4" />
                                                <span className="font-medium">{exp.role}</span>
                                            </div>
                                        </div>
                                        <div className={`w-fit px-4 py-2 ${getPillClasses(exp.color)} rounded-full text-sm font-semibold`}>
                                            {exp.period}
                                        </div>
                                    </div>

                                    <div className="mt-6 grid gap-3 sm:grid-cols-2">
                                        {exp.achievements.map((achievement, achIndex) => (
                                            <div key={achIndex} className="flex items-start gap-3">
                                                <div className={`mt-2 h-2 w-2 rounded-full ${getDotClass(exp.color)}`} />
                                                <span className="text-sm sm:text-base text-slate-700 leading-snug">{achievement}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

const ProjectsSection = () => {
    const projects = [
        {
            title: "Coldstone Creamery",
            subtitle: "Cross-border ice cream ordering app (Nigeria & Kenya)",
            tags: ["React Native", "React Query", "JWT"],
            outcomes: ["10,000+ active users", "4.5★ rating lift", "Faster iteration via OTA releases"]
        },
        {
            title: "GoSource",
            subtitle: "B2B ingredient sourcing platform",
            tags: ["React", "GraphQL", "TypeScript"],
            outcomes: ["Reduced over-fetching by ~45%", "Lowered incomplete submissions by 30%", "Automated onboarding flows"]
        },
        {
            title: "DaashApp",
            subtitle: "Inventory management dashboards",
            tags: ["React", "TanStack Query", "React Hook Form"],
            outcomes: ["Real-time dashboards", "Faster load times (-40%)", "Scalable forms + validation"]
        },
        {
            title: "AI Co-Pilot Platform",
            subtitle: "Creator growth decision interfaces",
            tags: ["React", "TypeScript", "Async UX"],
            outcomes: ["Reduced decision latency (~60%)", "Reusable component library", "Clean recomputation flows"]
        }
    ];

    return (
        <section id="projects" className="scroll-mt-24 bg-slate-50 py-24">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Selected Work</h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-600">
                        A few highlights across mobile, web, data dashboards, and AI product surfaces.
                    </p>
                </div>

                <div className="mt-14 grid gap-6 md:grid-cols-2">
                    {projects.map((project) => (
                        <div key={project.title} className="group rounded-3xl border border-slate-200 bg-white p-8 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between gap-6">
                                <div>
                                    <div className="text-xl font-bold text-slate-900">{project.title}</div>
                                    <div className="mt-2 text-slate-600">{project.subtitle}</div>
                                </div>
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 group-hover:bg-slate-100 transition-colors">
                                    <ArrowUpRight className="h-5 w-5" />
                                </div>
                            </div>

                            <div className="mt-5 flex flex-wrap gap-2">
                                {project.tags.map((tag) => (
                                    <span key={tag} className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700">
                                        {tag}
                                    </span>
                                ))}
                            </div>

                            <div className="mt-6 space-y-2">
                                {project.outcomes.map((item) => (
                                    <div key={item} className="flex items-start gap-3">
                                        <div className="mt-2 h-2 w-2 rounded-full bg-slate-900" />
                                        <div className="text-sm sm:text-base text-slate-700 leading-snug">{item}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const EducationSection = () => {
    return (
        <section id="education" className="scroll-mt-24 bg-white py-24">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Education</h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-600">
                        Strong computer science foundation with proven academic performance and leadership.
                    </p>
                </div>

                <div className="mt-14 mx-auto max-w-4xl rounded-3xl border border-slate-200 bg-white p-10 shadow-sm">
                    <div className="flex flex-col gap-6 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-4">
                            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-50">
                                <GraduationCap className="h-6 w-6 text-slate-900" />
                            </div>
                            <div>
                                <div className="text-xl font-bold text-slate-900">BSc. Computer Science</div>
                                <div className="mt-1 text-slate-700">Caleb University, Lagos, Nigeria</div>
                                <div className="mt-1 text-sm text-slate-600">Oct 2018 – Aug 2022</div>
                            </div>
                        </div>

                        <div className="w-fit rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-sm font-semibold text-slate-700">
                            GPA: 3.6 / 4.00 (Top 10 of 158)
                        </div>
                    </div>

                    <div className="mt-8 grid gap-3 sm:grid-cols-2">
                        <div className="flex items-start gap-3">
                            <div className="mt-2 h-2 w-2 rounded-full bg-slate-900" />
                            <div className="text-sm sm:text-base text-slate-700">Led peer programming tutorials, improving pass rates across core technical courses.</div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="mt-2 h-2 w-2 rounded-full bg-slate-900" />
                            <div className="text-sm sm:text-base text-slate-700">Recognized for academic excellence in multiple semesters (Software Engineering, Data Structures).</div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const ContactSection = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: ''
    });
    const [status, setStatus] = useState('idle');
    const [errorMessage, setErrorMessage] = useState('');

    const handleSubmit = (e) => {
        e.preventDefault();
        setStatus('sending');
        setErrorMessage('');

        emailjs.send(
            process.env.REACT_APP_EMAILJS_SERVICE_ID,
            process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
            {
                from_name: formData.name,
                from_email: formData.email,
                message: formData.message,
            },
            process.env.REACT_APP_EMAILJS_PUBLIC_KEY
        )
            .then(() => {
                setStatus('sent');
                setFormData({ name: '', email: '', message: '' });
            })
            .catch((error) => {
                console.error('Email failed:', error);
                setStatus('error');
                setErrorMessage('Failed to send message. Please try again.');
            });
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <section id="contact" className="scroll-mt-24 bg-slate-950 py-24">
            <div className="mx-auto max-w-screen-xl px-4 sm:px-6">
                <div className="mx-auto max-w-3xl text-center">
                    <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white">Contact</h2>
                    <p className="mt-4 text-base sm:text-lg text-slate-300">
                        Let’s build something fast, reliable, and user-friendly.
                    </p>
                </div>

                <div className="mt-14 grid gap-6 lg:grid-cols-2">
                    <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
                        <h3 className="text-xl font-bold text-white">Contact Info</h3>

                        <div className="mt-6 space-y-5">
                            <a href="mailto:kehindesalaudeen222@gmail.com" className="group flex items-center gap-4 rounded-2xl bg-white/5 p-4 hover:bg-white/10 transition-colors">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                                    <Mail className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <div className="text-sm text-slate-300">Email</div>
                                    <div className="text-white font-medium group-hover:underline">kehindesalaudeen222@gmail.com</div>
                                </div>
                            </a>

                            <a href="tel:+2348085503290" className="group flex items-center gap-4 rounded-2xl bg-white/5 p-4 hover:bg-white/10 transition-colors">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                                    <Phone className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <div className="text-sm text-slate-300">Phone</div>
                                    <div className="text-white font-medium group-hover:underline">+234 808 550 3290</div>
                                </div>
                            </a>

                            <div className="flex items-center gap-4 rounded-2xl bg-white/5 p-4">
                                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10">
                                    <MapPin className="h-5 w-5 text-white" />
                                </div>
                                <div>
                                    <div className="text-sm text-slate-300">Location</div>
                                    <div className="text-white font-medium">Lagos, Nigeria</div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8">
                            <div className="text-sm font-semibold text-white">Online</div>
                            <div className="mt-4 flex flex-wrap gap-3">
                                <a href="https://linkedin.com/in/kehnydev" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 transition-colors">
                                    <Linkedin className="h-4 w-4" />
                                    LinkedIn
                                    <ArrowUpRight className="h-4 w-4" />
                                </a>
                                <a href="https://github.com/kenicodex" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 transition-colors">
                                    <Github className="h-4 w-4" />
                                    GitHub
                                    <ArrowUpRight className="h-4 w-4" />
                                </a>
                                <a href="https://khenny-portfolio.netlify.app" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-white hover:bg-white/15 transition-colors">
                                    <ExternalLink className="h-4 w-4" />
                                    Portfolio
                                    <ArrowUpRight className="h-4 w-4" />
                                </a>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur">
                        <h3 className="text-xl font-bold text-white">Send a Message</h3>

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-slate-200">Name</label>
                                <input
                                    id="name"
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Your name"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-slate-200">Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="your.email@example.com"
                                    required
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-slate-200">Message</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    rows="5"
                                    className="mt-2 w-full rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                    placeholder="Tell me about your project or opportunity..."
                                    required
                                />
                            </div>

                            {status === 'sent' && (
                                <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4 text-sm text-emerald-200">
                                    Message sent successfully. I’ll get back to you soon.
                                </div>
                            )}
                            {status === 'error' && (
                                <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-4 text-sm text-rose-200">
                                    {errorMessage}
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={status === 'sending'}
                                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-blue-500 to-purple-600 px-5 py-3 font-semibold text-white shadow-sm hover:opacity-95 disabled:opacity-60"
                            >
                                {status === 'sending' ? 'Sending…' : 'Send Message'}
                                <ArrowUpRight className="h-5 w-5" />
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};
const App = () => {
    return (
        <div id="top" className="font-sans bg-white text-slate-900" style={{ scrollBehavior: 'smooth' }}>
            <Navbar />
            <HeroSection />
            <AboutSection />
            <SkillsSection />
            <ExperienceSection />
            <ProjectsSection />
            <EducationSection />
            <ContactSection />

            <footer className="bg-slate-950 text-white py-10">
                <div className="mx-auto max-w-screen-xl px-4 sm:px-6 flex flex-col items-center justify-between gap-4 sm:flex-row">
                    <div className="text-sm text-slate-300">© {new Date().getFullYear()} Kehinde Salaudeen. All rights reserved.</div>
                    <div className="text-sm text-slate-400">Built with React & Tailwind CSS</div>
                </div>
            </footer>
        </div>
    );
};

export default App;
